import { PRIMARY_MENU, PRIMARY_MENU_SKI } from 'components/navigation/config.js';
import { IApiAdTargeting } from 'redux-state/advertisement/types.js';
import { isDevEnv } from 'utilities/consts/webpack-consts.js';
import { getWindow, getWindowLocation } from 'utilities/window.js';

export type NeilsenTrackArgs = { pathname: string; isSkiSeason?: string; targeting?: IApiAdTargeting[] };

const SECTION_OTHER = { name: 'Metservice.com' };

const getAPI = (keys: Array<Nielsen.NIELSEN_API_DATA>, pathname: string) => {
  const index = keys.findIndex(({ pathname: pathnames }) =>
    pathnames.some((path) => new RegExp(`^${path}`).test(`/${pathname.replace(/^\/*/, '')}`)),
  );

  const { key } = keys[index === -1 ? keys.length - 1 : index];

  return { brand: key.split('/')[0], other: [keys.length - 1].includes(index) };
};
const createMetadata = ({
  pathname,
  isSkiSeason,
  targeting,
}: {
  pathname: string;
  isSkiSeason?: string;
  targeting?: IApiAdTargeting[];
}): Nielsen.Metadata => {
  const API_KEYS = getWindow()?.Nielsen?.API_KEYS ?? [];
  const { brand, other } = getAPI(API_KEYS, pathname);
  const menu = isSkiSeason === 'true' ? PRIMARY_MENU_SKI : PRIMARY_MENU;

  const { name } = other
    ? SECTION_OTHER
    : menu.find(({ link }) => {
        if (link === '/') {
          return pathname === 'national';
        }

        return new RegExp(`^${link}`).test(pathname);
      }) || SECTION_OTHER;

  const section = `${name.replace(/\s/g, '')}-${targeting?.find(({ key }) => key === 'type')?.value || 'other'}`;

  return {
    type: 'static',
    assetid: pathname,
    section,
    subbrand: brand,
    pageURL: getWindowLocation().href,
  };
};

export function NielsenInit(): (args: NeilsenTrackArgs) => Promise<void> {
  let retry = 0;
  let interval = 0;
  let sdk: Nielsen.SDK | undefined = undefined;
  let metadata: Nielsen.Metadata | undefined = undefined;
  const queue: (() => void)[] = [];

  const isSDKLoaded = () => Boolean(getWindow()?.sdkInstance);

  const checkIfSDKLoaded = () => {
    if (interval) {
      clearInterval(interval);
    }
    return new Promise<void>((resolve, reject) => {
      interval = setInterval(() => {
        if (isSDKLoaded()) {
          clearInterval(interval);
          sdk = getWindow()?.sdkInstance;
          resolve();
        }

        if (++retry === 3) {
          reject('Error');
        }
      }, 1000) as unknown as number;
    });
  };

  const trackerCB = function ({
    pathname,
    isSkiSeason,
    targeting,
  }: {
    pathname: string;
    isSkiSeason?: string;
    targeting?: IApiAdTargeting[];
  }) {
    // Nielsen V60
    if (!isDevEnv(false)) {
      getWindow()?.trac?.pageEvent(pathname);
    }

    // ga.pageview(pathname, [], document.title);

    if (!sdk) {
      return;
    }

    if (metadata) {
      __NIELSEN_SHOW_DEBUGGING__ === 'true' && console.log('NIELSEN / staticend / ', metadata);
      sdk.ggPM('staticend', metadata);
    }

    metadata = createMetadata({ pathname, isSkiSeason, targeting });

    __NIELSEN_SHOW_DEBUGGING__ === 'true' && console.log('NIELSEN / staticstart / ', metadata);
    sdk.ggPM('staticstart', metadata);
  };

  return async (args: NeilsenTrackArgs) => {
    if (isSDKLoaded()) {
      if (!sdk) {
        sdk = getWindow()?.sdkInstance;
      }

      trackerCB(args);
    } else {
      queue.push(() => trackerCB(args));
      try {
        await checkIfSDKLoaded();
      } catch (e) {
        console.error('Error loading Nielsen SDK');
      }

      while (queue.length) {
        queue.shift()?.();
      }
    }
  };
}
